import React, { memo, useCallback, useMemo } from "react";

import {
  Button,
  Center,
  FormControl,
  FormErrorMessage,
  HStack,
  IconButton,
  InputGroup,
  Modal,
  ModalBody,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Text,
  Textarea,
  VStack,
} from "@chakra-ui/react";
import { useMutation, useQuery } from "@apollo/client";
import colors from "app/colors";
import { FieldValues, useForm } from "react-hook-form";
import LoadingModal from "./loading-modal";
import {
  EditMessageThreadSubjectMutation,
  EditMessageThreadSubjectMutationVariables,
  MessageThreadsQuery,
  MessageThreadsQueryVariables,
  MessageThreadType,
} from "gql/graphql";
import EDIT_MESSAGE_THREAD_SUBJECT_MUTATION from "./networking/edit-message-thread-subject";
import { getMessageFromError } from "modules/utils/Functions";
import { CloseIcon } from "modules/icons";
import MESSAGE_THREADS_QUERY from "modules/messaging/_networking/message-threads";
import { useOverlay } from "modules/providers/overlay-provider";

interface IProps {
  messageThreadId: string;
}

const MessageThreadEditSubjectModal = ({ messageThreadId }: IProps) => {
  const { closeModal } = useOverlay();
  const { data, error } = useQuery<
    MessageThreadsQuery,
    MessageThreadsQueryVariables
  >(MESSAGE_THREADS_QUERY, {
    variables: {
      id: messageThreadId,
    },
  });
  const [editMessageThreadSubjectAPI] = useMutation<
    EditMessageThreadSubjectMutation,
    EditMessageThreadSubjectMutationVariables
  >(EDIT_MESSAGE_THREAD_SUBJECT_MUTATION);

  const {
    handleSubmit,
    formState: { errors },
    register,
    resetField,
  } = useForm({
    mode: "onSubmit",
  });

  const messageThread = useMemo(() => data?.messageThreads[0], [data]);
  const onSubmit = useCallback(
    async ({ subject }: FieldValues) => {
      editMessageThreadSubjectAPI({
        variables: {
          id: messageThreadId,
          subject: subject || "", //forces backend to regenerate subject for chats
        },
      });
      closeModal();
    },
    [closeModal, editMessageThreadSubjectAPI, messageThreadId],
  );

  const onClearClicked = useCallback(() => {
    resetField("subject");
  }, [resetField]);

  const headerText = useMemo(() => {
    switch (messageThread?.type) {
      case MessageThreadType.Chat:
        return "Edit Chat Name";
      default:
        return "Edit Subject";
    }
  }, [messageThread?.type]);

  const placeHolder = useMemo(() => {
    switch (messageThread?.type) {
      case MessageThreadType.Chat:
        return "Add chat name";
      default:
        return "Add thread subject";
    }
  }, [messageThread?.type]);

  if (error) {
    return <Text>{error.message}</Text>;
  }

  if (!messageThread) return <LoadingModal />;

  return (
    <Modal isOpen onClose={closeModal} size="xs" isCentered>
      <ModalOverlay />
      <ModalContent borderRadius={16}>
        <ModalHeader>
          <Center>
            <Text fontFamily="Outfit" fontSize={20} fontWeight={700}>
              {headerText}
            </Text>
          </Center>
        </ModalHeader>
        <ModalBody>
          <form id="edit-subject-form" onSubmit={handleSubmit(onSubmit)}>
            <FormControl isInvalid={!!errors?.subject}>
              <VStack spacing={8}>
                <InputGroup>
                  <HStack
                    w="full"
                    spacing={0}
                    alignItems="flex-start"
                    py={2}
                    px={2}
                    borderRadius={8}
                    borderColor={colors.gray60}
                    borderWidth={1}
                  >
                    <Textarea
                      id="subject"
                      placeholder={placeHolder}
                      maxLength={60}
                      p={0}
                      border="none"
                      resize="none"
                      _focusVisible={{ border: "none", boxShadow: "none" }}
                      defaultValue={
                        messageThread.subject
                          ? messageThread.subject
                          : undefined
                      }
                      {...register("subject", {
                        maxLength: {
                          value: 60,
                          message: "The subject must be 60 characters or less",
                        },
                      })}
                    />
                    <IconButton
                      variant="link"
                      size="sm"
                      p={0}
                      icon={<CloseIcon fill={colors.gray30} boxSize={5} />}
                      onClick={onClearClicked}
                      aria-label="clear subject"
                    />
                  </HStack>
                </InputGroup>
                <FormErrorMessage>
                  {getMessageFromError(errors?.subject?.message)}
                </FormErrorMessage>
                <HStack pb={6}>
                  <Button
                    variant="cancel"
                    height={12}
                    w={28}
                    onClick={(e) => {
                      e.stopPropagation();
                      closeModal();
                    }}
                  >
                    Cancel
                  </Button>
                  <Button height={12} w={28} type="submit">
                    Save
                  </Button>
                </HStack>
              </VStack>
            </FormControl>
          </form>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export default memo(MessageThreadEditSubjectModal);
